// Added by Sejune Oh on 2020-03-02
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { FC, forwardRef, useState, ReactText, ReactNode, useContext, useEffect, useCallback } from 'react'
import { makeStyles, useTheme, createStyles } from '@material-ui/core'
import { useSnackbar, VariantType } from 'notistack'
import classNames from 'classnames'

// Context
import { NotiStackContext } from './NotiStackProvider'

// ui
import { Collapse, Paper, Typography, Card, CardActions, IconButton, CardProps } from '@/components/_primes/v2'
import { CloseIcon, ExpandMoreIcon } from '@/components/_primes'
import { SuccessIcon, InfoIcon, WarningIcon, ErrorIcon } from './NotiStackIcon'

// helper
import { NOTI_AUTO_HIDE_DURATION } from '@/helper/define'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
interface NotiStackProps {
  id: ReactText
  message: ReactNode
}

export interface NotiStackOptionProps {
  variant: VariantType
  subMessage?: string
}

const NotiStack: FC<NotiStackProps> = forwardRef<CardProps, NotiStackProps>(({ id, message, ...props }, ref) => {
  const theme = useTheme()
  const classes = useStyles(theme)

  const { closeSnackbar } = useSnackbar()
  const { unsetNotiInfo, getNotiInfo, changeAutoHideDuration } = useContext(NotiStackContext)!

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // ! States
  const [expanded, setExpanded] = useState(false)
  const [variant, setVariant] = useState<VariantType>('default')
  const [subMessage, setSubMessage] = useState<string | null>(null)

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // ! Functions
  const handleExpandClick = () => setExpanded(!expanded)
  const handleDismiss = () => {
    unsetNotiInfo(id as string)
    closeSnackbar(id)
  }
  const getIcon = useCallback(() => {
    if (variant === 'error') return <ErrorIcon></ErrorIcon>
    if (variant === 'success') return <SuccessIcon></SuccessIcon>
    if (variant === 'info') return <InfoIcon></InfoIcon>
    if (variant === 'warning') return <WarningIcon></WarningIcon>
    return null
  }, [variant])

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // ! Effects
  useEffect(() => {
    changeAutoHideDuration(NOTI_AUTO_HIDE_DURATION)
    const curNotice = getNotiInfo(id as string)!
    if (!curNotice) return

    setVariant(curNotice.variant)
    setSubMessage(curNotice.subMessage)
  // eslint-disable-next-line
  }, [])

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // ! Render
  return (
    <Card className={classes.card} ref={ref}>
      <CardActions className={classes.cardActionWrapper} data-type={variant}>
        <div className={classes.iconWrapper}>{getIcon()}</div>
        <Typography className={classes.typography} customProps={{ 'data-cy-noti': variant }}>
          {message}
        </Typography>
        <div className={classes.icons}>
          {subMessage && (
            <IconButton
              aria-label="Show more"
              className={classNames(classes.expand, { [classes.expandOpen]: expanded })}
              onClick={handleExpandClick}
            >
              <ExpandMoreIcon />
            </IconButton>
          )}
          <IconButton className={classes.expand} onClick={handleDismiss}>
            <CloseIcon />
          </IconButton>
        </div>
      </CardActions>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Paper className={classes.collapse}>
          <Typography gutterBottom>{subMessage}</Typography>
        </Paper>
      </Collapse>
    </Card>
  )
})

const useStyles = makeStyles((theme) =>
  createStyles({
    card: {
      maxWidth: '50vw',
      minWidth: 344,
    },
    typography: {
      marginLeft: '0 !important',
      flex: 1,
      fontSize: '1rem'
    },
    iconWrapper: {
      display: 'flex',
      opacity: '0.9',
      padding: '7px 0',
      fontSize: '22px',
      marginRight: '12px',
    },
    cardActionWrapper: {
      padding: '4px 4px 4px 12px',
      backgroundColor: 'rgb(49, 49, 49)',
      '&[data-type="default"]': {},
      '&[data-type="info"]': {
        backgroundColor: '#43a047',
      },
      '&[data-type="success"]': {
        backgroundColor: '#145c82',
      },
      '&[data-type="warning"]': {
        backgroundColor: '#ffa000',
      },
      '&[data-type="error"]': {
        backgroundColor: '#c04646',
      },
    },
    icons: {
      marginLeft: 'auto',
    },
    expand: {
      padding: '8px 8px',
      transform: 'rotate(0deg)',
      transition: theme.transitions.create('transform', {
        duration: theme.transitions.duration.shortest,
      }),
    },
    expandOpen: {
      transform: 'rotate(180deg)',
    },
    collapse: {
      padding: '16px',
      backgroundColor: '#272b2e',
    },
    checkIcon: {
      fontSize: 20,
      color: '#b3b3b3',
      paddingRight: 4,
    },
    button: {
      padding: 0,
      textTransform: 'none',
    },
  })
)

export default NotiStack
